import React, { useState } from 'react';
import api from '../api';
import { Send, AlertCircle, CheckCircle } from 'lucide-react';

const ModeratorRequestForm = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    contactInfo: '',
    experience: '',
    zipcode: '',
    chapter: 'default',
    city: '',
    requestedRadiusMeters: '5000'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (!formData.contactInfo || !formData.experience || !formData.zipcode || !formData.city || !formData.requestedRadiusMeters) {
      setError('All fields are required');
      setLoading(false);
      return;
    }

    const radius = Number(formData.requestedRadiusMeters);
    if (!Number.isFinite(radius) || radius <= 0) {
      setError('Please enter a valid requested radius in meters.');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/api/moderator-request', formData);
      setSuccess(response.data.message);
      
      setTimeout(() => {
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Request Moderator Access</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <CheckCircle size={16} />
              <span>{success}</span>
            </div>
          )}

          <p className="form-description">
            Help keep our community safe by becoming a moderator. Please provide the following information:
          </p>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="contactInfo">Contact Information *</label>
              <input
                type="text"
                id="contactInfo"
                name="contactInfo"
                value={formData.contactInfo}
                onChange={handleChange}
                placeholder="Email, phone, or preferred contact method"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="experience">Experience & Motivation *</label>
              <textarea
                id="experience"
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                placeholder="Tell us about your experience with community moderation, safety advocacy, or why you want to be a moderator..."
                rows={5}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="zipcode">Zipcode *</label>
              <input
                type="text"
                id="zipcode"
                name="zipcode"
                value={formData.zipcode}
                onChange={handleChange}
                placeholder="Your zipcode"
                maxLength={10}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="city">City *</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="e.g., San Jose, CA"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="requestedRadiusMeters">Requested Coverage Radius (meters) *</label>
              <input
                type="number"
                id="requestedRadiusMeters"
                name="requestedRadiusMeters"
                value={formData.requestedRadiusMeters}
                onChange={handleChange}
                placeholder="5000"
                min={100}
                step={100}
                required
              />
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Submitting...' : (
                  <>
                    <Send size={16} />
                    Submit Request
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ModeratorRequestForm;
